<template>
  <div class="c-our-segment-filter-select">
    <BittsPopover
      v-model:open="isOpen"
      placement="bottomLeft"
      :mount-to-body="true"
    >
      <div
        role="combobox"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :tabindex="0"
        class="main-select"
        :class="{
          open: isOpen,
          disabled: props.disabled,
        }"
        @click="openDropdown"
        @keydown.enter="openDropdown"
        @keydown.space.prevent="openDropdown"
      >
        <div
          v-if="!hasSelections"
          class="c-our-segment-filter-select__placeholder"
        >
          Select populations
        </div>

        <div
          v-else
          ref="selectedItemsContainer"
          class="c-our-segment-filter-select__selected-items"
        >
          <BittsTag
            v-for="(chip, index) in chips"
            :key="chip.segment"
            :ref="(el) => setTagRef(el, index)"
            color="neutral"
            size="x-small"
            class="c-our-segment-filter-select__tag"
            :class="{
              'c-our-segment-filter-select__tag--hidden': index >= visibleCount,
            }"
            @click.stop="!props.disabled && removeSegment(chip.segment)"
          >
            <BittsTooltip
              placement="topLeft"
              :mount-to-body="true"
              :show-tooltip="
                resolvedSegments.some(
                  (s) => s.type === chip.segment && s.populations.length,
                )
                  ? null
                  : false
              "
            >
              <div class="inline-flex items-center gap-4 text-sm">
                <span>{{ chip.label }}</span>
                <FontAwesomeIcon :icon="['fak', 'x']" />
              </div>
              <template #title>
                <AccountMappingFiltersPopulationsHover
                  :show-segment-header="false"
                  :resolved-segments="
                    resolvedSegments.filter((s) => s.type === chip.segment)
                  "
                />
              </template>
            </BittsTooltip>
          </BittsTag>

          <BittsTooltip v-if="overflowCount > 0" :mount-to-body="true">
            <BittsTag
              ref="overflowTag"
              size="x-small"
              color="neutral"
              class="c-our-segment-filter-select__overflow-tag"
            >
              +{{ overflowCount }}
            </BittsTag>
            <template #title>
              <div class="flex flex-col gap-4">
                <div
                  v-for="chip in overflowChips"
                  :key="chip.segment"
                  class="flex items-start gap-4"
                >
                  <span
                    >{{ chip.label }}: {{ chip.populations.join(', ') }}</span
                  >
                </div>
              </div>
            </template>
          </BittsTooltip>
        </div>

        <div class="controls">
          <FontAwesomeIcon
            :icon="['fak', isOpen ? 'chevron-up' : 'chevron-down']"
            class="text-neutral-text-placeholder cursor-pointer"
          />
        </div>
      </div>

      <template #content>
        <div v-if="isOpen" class="w-full z-50">
          <PopulationFilterTree
            :selected-filters="model"
            :show-search="true"
            @update="handleUpdate"
          />
        </div>
      </template>
    </BittsPopover>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover, BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import AccountMappingFiltersPopulationsHover, {
  type ResolvedPopulationSegmentType,
} from '@/components/reports/AccountMappingFiltersPopulationsHover.vue';
import PopulationFilterTree from '@/components/reports/PopulationFilterTree.vue';

import { useChipOverflow } from '@/composables/reports/useChipOverflow';
import { useFeedsStore, usePopulationsStore, useSourcesStore } from '@/stores';
import {
  ALL_SEGMENTS,
  SEGMENTS_TO_LABEL_MAP,
  type SegmentType,
} from '@/types/populations';
import type { ReportOurSegmentFilters } from '@/types/reports';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const model = defineModel<ReportOurSegmentFilters>({
  default: () => ({ operator: 'OR', items: [] }),
});

const popsStore = usePopulationsStore();
const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();

const isOpen = ref(false);

const resolvedSegments = computed<ResolvedPopulationSegmentType[]>(() => {
  const items = model.value?.items ?? [];
  if (!items.length) return [];
  return ALL_SEGMENTS.flatMap((segmentType) => {
    const segmentItems = items.filter((item) => item.segment === segmentType);
    if (!segmentItems.length) return [];
    const allPops = popsStore.getPopulationsBySegment(segmentType);
    const filterIds = segmentItems.every((f) => f.population_ids?.length)
      ? segmentItems.flatMap((f) => f.population_ids ?? [])
      : undefined;
    const populations = allPops
      .filter((pop) => !filterIds || filterIds.includes(pop.id))
      .map((pop) => {
        const source = sourcesStore.getSourceById(pop.source_id ?? 0);
        const feed = feedsStore.getFeedById(source?.feed_id ?? 0);
        return {
          id: pop.id,
          name: pop.name,
          dataSourceType: feed?.integration.type,
        };
      });
    return populations.length
      ? [
          {
            type: segmentType,
            label: SEGMENTS_TO_LABEL_MAP[segmentType],
            populations,
          },
        ]
      : [];
  });
});

const chips = computed(() =>
  (model.value?.items ?? [])
    .map((item) => {
      const allPops = popsStore.getPopulationsBySegment(
        item.segment as SegmentType,
      );
      const populations = item.population_ids?.length
        ? item.population_ids
            .map((id) => allPops.find((p) => p.id === id)?.name)
            .filter(
              Boolean as unknown as (v: string | undefined) => v is string,
            )
        : allPops.map((p) => p.name);
      return {
        label: `${SEGMENTS_TO_LABEL_MAP[item.segment as SegmentType]} (${populations.length})`,
        populations,
        ...item,
      };
    })
    .filter((chip) => chip.populations.length > 0),
);

const hasSelections = computed(() => chips.value.length > 0);

const { selectedItemsContainer, overflowTag, visibleCount, setTagRef } =
  useChipOverflow(chips);

const overflowChips = computed(() => chips.value.slice(visibleCount.value));
const overflowCount = computed(() => overflowChips.value.length);

function openDropdown() {
  if (props.disabled) return;
  isOpen.value = true;
}

function handleUpdate(val: ReportOurSegmentFilters) {
  model.value = val;
}

function removeSegment(segment: string) {
  model.value = {
    ...model.value,
    items: model.value.items.filter((item) => item.segment !== segment),
  };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.c-our-segment-filter-select {
  @apply flex-1 min-w-[120px];
}

.main-select {
  @apply flex items-center justify-between gap-4 px-8 py-4
    border border-neutral-border-strong rounded-r-lg bg-white cursor-pointer
    min-h-[32px] min-w-0;

  &:hover:not(.disabled),
  &.open {
    @apply border-info-border outline-none z-10 relative;
  }

  &.disabled {
    @apply opacity-60 cursor-default;
  }
}

.c-our-segment-filter-select__placeholder {
  @apply text-neutral-text-placeholder text-sm truncate;
}

.c-our-segment-filter-select__selected-items {
  @apply flex-1 flex flex-nowrap gap-4 overflow-hidden;
}

.c-our-segment-filter-select__tag {
  @apply inline-flex items-center gap-4 shrink-0 cursor-pointer pointer-events-auto;
}

.c-our-segment-filter-select__tag--hidden {
  @apply hidden;
}

.c-our-segment-filter-select__overflow-tag {
  @apply inline-flex items-center shrink-0;
}

.controls {
  @apply flex items-center shrink-0 ml-auto;
}
</style>
